import classNames from "classnames";
import Image from "components/imageNew/image";
import Button from "components/buttonNew/button";
import IconButton from "components/iconButton/iconButton";
import { CrossNewIcon } from "components/icons/icons";
import { useMeasure } from "react-use";
import { useEffect, useState } from "react";
import { useBannerHeight } from "lib/context/bannerContext";
import { Banner } from "lib/types/banners";
import { trackGTMEvent } from "lib/utils";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";

interface BottomBannerContentProps {
  banner: Banner;
}

export const SESSION_STORAGE_KEY = "bottomBanner";

const BANNER_BUTTON_COLOUR: Record<Banner["theme"], "cerise" | "navy"> = {
  black: "cerise",
  cerise: "cerise",
  blue: "navy",
};

export default function BottomBannerContent({
  banner,
}: BottomBannerContentProps) {
  const { track } = useMixpanelTracking();
  const [bannerRef, { height }] = useMeasure<HTMLDivElement>();
  const [closed, setClosed] = useState(
    () =>
      window.sessionStorage.getItem(`${SESSION_STORAGE_KEY}/${banner.id}`) ===
      "closed"
  );
  const { setBottomBannerHeight } = useBannerHeight();

  // A closed banner is unmounted, so useMeasure keeps reporting its last height.
  const visibleHeight = closed ? 0 : height;

  useEffect(() => {
    const footer: HTMLElement | null = document.querySelector(".js-footer-gap");

    if (footer) {
      let prevPadding: string | undefined;

      if (footer.dataset.paddingBottom) {
        prevPadding = footer.dataset.paddingBottom;
      } else {
        prevPadding = window
          .getComputedStyle(footer)
          .getPropertyValue("padding-bottom");
        footer.dataset.paddingBottom = prevPadding;
      }

      footer.style.paddingBottom = `${visibleHeight + parseInt(prevPadding)}px`;
    }
  }, [visibleHeight]);

  // Update banner height in context whenever it changes
  useEffect(() => {
    setBottomBannerHeight(visibleHeight);
  }, [visibleHeight, setBottomBannerHeight]);

  const onCloseBanner = () => {
    setClosed(true);

    window.sessionStorage.setItem(
      `${SESSION_STORAGE_KEY}/${banner.id}`,
      "closed"
    );
  };

  const onPrimaryButtonClick = async (banner: Banner) => {
    trackGTMEvent({
      event: "Banner primary button click",
      bannerType: "bottom_banner",
      bannerName: banner.name,
      bannerId: banner.id,
      clickLink: banner.primaryButtonUrl,
      clickText: banner.primaryButtonLabel,
      bannerEventAction: "Banner primary button click",
    });

    await track("Banner primary button click", {
      "Banner name": banner.name,
      "Banner id": banner.id,
      "Click link": banner.primaryButtonUrl,
      "Click text": banner.primaryButtonLabel,
      Component: "BottomBanner",
    });
  };

  if (closed) return null;

  return (
    <div
      ref={bannerRef}
      data-testid={"bottomBanner__outerWrapper"}
      className={classNames(
        "fixed bottom-0 left-0 right-0 z-10 h-auto w-[calc(var(--vw,_1vw)_*_100)] print:hidden",
        "js-fixed-exclude",
        {
          "bg-gradient-to-r from-black to-[#333333] text-white":
            banner.theme === "black",
          "bg-[#FBE7EF] text-black-rock": banner.theme === "cerise",
          "bg-[#EBEDFB] text-black-rock": banner.theme === "blue",
        }
      )}
    >
      <div
        data-testid="bottomBanner"
        className={
          "px-[48px] pb-5 pt-4 md:px-5 md:pb-6 md:pt-6 lg:px-[100px] lg:pb-5 lg:pt-5"
        }
      >
        <div
          aria-hidden={"true"}
          className={classNames(
            "absolute bottom-0 left-0 right-0 h-[2px] border-b-[0.5px] border-dashed border-b-[#62C655]",
            {
              "bg-[#FFFB47]": banner.theme === "black",
              "bg-cerise": banner.theme === "cerise",
              "bg-pro-blue": banner.theme === "blue",
            }
          )}
        />
        <div
          className={
            "m-auto flex max-w-[1220px] flex-col items-center justify-start md:flex-row"
          }
        >
          {banner.mainImage && (
            <div
              className={
                "mb-[10px] flex-[0_0_auto] md:mb-0 md:mr-5 lg:mr-[87px]"
              }
            >
              <Image
                className={"h-fit w-[77px] md:w-[154px]"}
                src={banner.mainImage.url}
                width={banner.mainImage.width}
                height={banner.mainImage.height}
                sizes="min(400px, 100vw)"
                alt={banner.mainImage.alt || `Bottom banner image`}
                quality={75}
                placeholder="blur"
                blurDataURL={banner.mainImage.blurUpThumb || undefined}
              />
            </div>
          )}
          <section
            className={
              "flex flex-col items-center gap-4 md:w-full md:flex-row md:items-start md:justify-between md:gap-8 lg:items-center"
            }
          >
            <div
              className={"flex flex-col gap-[10px] text-center md:text-left"}
            >
              {banner.heading && (
                <h4 className={"font-bold text-[16px] leading-[130%]"}>
                  {banner.heading}
                </h4>
              )}
              {banner.text && (
                <p
                  className={
                    "hidden text-[16px] leading-[130%] md:block md:pr-5"
                  }
                >
                  {banner.text}
                </p>
              )}
            </div>
            <div
              className={
                "flex flex-[0_0_auto] flex-col-reverse items-end gap-x-3 gap-y-2 lg:block"
              }
            >
              {banner.primaryButtonLabel && banner.primaryButtonUrl && (
                <div className={"flex-[0_0_auto]"}>
                  <Button
                    size={"medium"}
                    className={"text-center lg:w-[210px]"}
                    colour={BANNER_BUTTON_COLOUR[banner.theme]}
                    component={"link"}
                    target={"_blank"}
                    rel="noreferrer"
                    href={banner.primaryButtonUrl}
                    onClick={async () => await onPrimaryButtonClick(banner)}
                  >
                    {banner.primaryButtonLabel}
                  </Button>
                </div>
              )}
              <div
                className={
                  "absolute right-[10px] top-[10px] md:relative md:right-0 md:top-0 lg:absolute lg:right-[26px] lg:top-[22px]"
                }
              >
                <IconButton
                  color={banner.theme === "black" ? "white-cerise" : "black"}
                  variant={"square"}
                  onClick={onCloseBanner}
                  aria-label={"Close banner"}
                >
                  <span className={"flex h-full w-full items-center"}>
                    <CrossNewIcon width={24} height={24} />
                  </span>
                </IconButton>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
